/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, June 4th 2022, 4:59:49 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Stack from "@mui/material/Stack";
import ListItemText from "@mui/material/ListItemText";
import { Team } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { EmptyTeamsInputCTA } from "./EmptyTeamsInputCTA";
import {
  APIMany2ManyTransferListFormInput,
  ConcreteAPIMany2ManyTransferListFormInputProps,
  APIMany2ManyCheckBoxFormInput,
  ConcreteAPIMany2ManyInputProps,
} from "ui-widgets/forms";
import { AsyncAPIListAutoComplete, OSAvatar, OSActionButton } from "ui-widgets";
import { NewTeamButton } from "teams/components/NewTeamButton";

interface TeamSelectorInputProps {
  chosenTeam: Team;
  setChosenTeam: React.Dispatch<React.SetStateAction<Team>>;
}

export const TeamSelectorInput = ({ chosenTeam, setChosenTeam }: TeamSelectorInputProps) => {
  const getTeamsOptionLabel = (option: Team) => {
    if (!option?.team_name) return;

    const memberText = option.members?.length === 1 ? "member" : "members";

    return `${option.team_name} ${option.members?.length > 0 ? `(${option.members.length} ${memberText})` : ""}`;
  };

  return (
    <Stack direction="row" spacing={1}>
      <AsyncAPIListAutoComplete<Team>
        id="team-select-dropdown"
        label="Select Team"
        itemIdProperty="id"
        getOptionLabel={getTeamsOptionLabel}
        loadingText="Loading Teams"
        onChangeCallBack={(selectedTeam) => setChosenTeam(selectedTeam)}
        value={chosenTeam ?? ({} as Team)}
        apiServicePath={APISERVICEPATHS.teams}
        disableClearable
        inputStartAdornment={
          <OSAvatar name={chosenTeam?.team_name} variant="rounded" sx={{ width: 28, height: 28 }}></OSAvatar>
        }
        tooltipProps={{ placement: "top" }}
        fullWidth
        noOptionsText="No Teams exist for your organization yet. Add a new team first."
      />
      <NewTeamButton updateTeamsList={(updatedTeam) => {}}>
        {({ onClick }) => <OSActionButton actionStyle="add" buttonType="floating" size="large" onClick={onClick} />}
      </NewTeamButton>
    </Stack>
  );
};

export const TeamsTransferListInput = <T,>(props: ConcreteAPIMany2ManyTransferListFormInputProps<T, Team>) => (
  <APIMany2ManyTransferListFormInput<T, Team>
    schema_field_key="team_ids"
    itemIdProperty="id"
    getOptionLabel={(option) => <ListItemText primary={`${option.team_name} (${option.members.length})`} />}
    apiServicePath={APISERVICEPATHS.teams}
    {...props}
  />
);

export const TeamsCheckboxesInput = <T,>(props: ConcreteAPIMany2ManyInputProps<T, Team>) => (
  <APIMany2ManyCheckBoxFormInput<T, Team>
    apiServicePath={APISERVICEPATHS.teams}
    itemIdProperty="id"
    getOptionComponent={(option) => (
      <ListItemText
        primary={option.team_name}
        secondary={`${option.members.length} ${option.members.length === 1 ? "member" : "members"}`}
      />
    )}
    noDataMessage={<EmptyTeamsInputCTA />}
    {...props}
  />
);

export const TeamsGroupsTransferListInput = <T,>(props: ConcreteAPIMany2ManyTransferListFormInputProps<T, Team>) => (
  <APIMany2ManyTransferListFormInput<T, Team>
    schema_field_key="team_ids"
    itemIdProperty="group"
    getOptionLabel={(option) => <ListItemText primary={`${option.team_name} (${option.members.length})`} />}
    apiServicePath={APISERVICEPATHS.teams}
    {...props}
  />
);
